import { z } from 'zod';

import { CLIENT_CONTEXT_MAX_TEXT_LENGTH } from '../client-context/fields.js';
import { ClientContextErrorCodes, type ClientContextFailure } from '../client-context/onboarding.js';
import { BRAND_BRAIN_NEXUS_COLUMNS, isBrandBrainNexusColumn, type BrandBrainNexusColumn } from './fields.js';

// ─── Nexus-owned Brand Brain fields: storage and validation ──────────
// Stored only in Postgres, in tenant_client_contexts beside the BigQuery copy.
// Never part of the client_context registry, a BigQuery query or an onboarding
// sync. Placement lives in `fields.ts`.

/**
 * Approved claims and disclaimers must read back exactly as written, so the
 * text is never trimmed or reflowed: spacing, punctuation and line breaks are
 * kept. Only an all-whitespace value is stored as NULL, so it stays a gap.
 */
const verbatimTextSchema = z
    .string()
    .max(CLIENT_CONTEXT_MAX_TEXT_LENGTH)
    .nullable()
    .transform((value) => (value === null || value.trim() === '' ? null : value));

export const BRAND_BRAIN_NEXUS_FIELD_SCHEMAS = {
    approved_claims: verbatimTextSchema,
    what_works: verbatimTextSchema,
    visual_production_rules: verbatimTextSchema,
} as const satisfies Record<BrandBrainNexusColumn, z.ZodTypeAny>;

export type BrandBrainNexusValues = Record<BrandBrainNexusColumn, string | null>;
export type BrandBrainNexusChanges = Partial<BrandBrainNexusValues>;

export type BrandBrainNexusValidation = { ok: true; changes: BrandBrainNexusChanges } | ClientContextFailure;

export function validateBrandBrainNexusChanges(changes: Record<string, unknown>): BrandBrainNexusValidation {
    const valid: BrandBrainNexusChanges = {};
    for (const [field, value] of Object.entries(changes)) {
        if (!isBrandBrainNexusColumn(field)) {
            return { ok: false, code: ClientContextErrorCodes.UNKNOWN_FIELD, message: `${field} is not a Brand Brain field`, field };
        }
        const parsed = BRAND_BRAIN_NEXUS_FIELD_SCHEMAS[field].safeParse(value);
        if (!parsed.success) {
            return {
                ok: false,
                code: ClientContextErrorCodes.VALIDATION_FAILED,
                message: `${field}: ${parsed.error.issues[0]?.message ?? 'Invalid value'}`,
                field,
            };
        }
        valid[field] = parsed.data;
    }
    return { ok: true, changes: valid };
}

/** Splits an edit's `changes` into the client_context columns and the Nexus-owned fields. */
export function splitBrandBrainChanges(changes: Record<string, unknown>): {
    clientContext: Record<string, unknown>;
    nexus: Record<string, unknown>;
} {
    const clientContext: Record<string, unknown> = {};
    const nexus: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(changes)) {
        if (isBrandBrainNexusColumn(field)) nexus[field] = value;
        else clientContext[field] = value;
    }
    return { clientContext, nexus };
}

export function emptyBrandBrainNexusValues(): BrandBrainNexusValues {
    return Object.fromEntries(BRAND_BRAIN_NEXUS_COLUMNS.map((column) => [column, null])) as BrandBrainNexusValues;
}
